import env from '@y2/environments';

import * as portsData from './ssr-services-ports.json';

type ServicesPorts = { [key: string]: number };

type ClusterInfo = {
  basePath: string;
  isMatchingCluster: (serviceBasePath: string) => boolean;
  ports: ServicesPorts;
  getClusterPath: (serviceName: string, port?: number) => string;
};

const mainPorts: ServicesPorts = portsData.main;
const drushimPorts: ServicesPorts = portsData.drushim;

const isLocal = env.name === 'local';
const isBrowser = typeof window !== 'undefined';

const mainClusterPath = env.routes.api.gwInClusterForSSR;
const drushimClusterPath = env.routes.api.gwDrushimInClusterForSSR;

const validateClusterEnvSetup = () => {
  const shouldSkipCheck = isLocal || isBrowser || env.isJestRun;
  if (shouldSkipCheck) return;

  if (!mainClusterPath || !drushimClusterPath) {
    throw Error(
      'missing NEXT_PUBLIC_CLUSTER_GW or NEXT_PUBLIC_CLUSTER_GW_DRUSHIM in the environment, needed to fetch requests in cluster for SSR phase',
    );
  }
};

validateClusterEnvSetup();

const clustersMap: Record<string, ClusterInfo> = {
  main: {
    basePath: env.routes.api.gw,
    isMatchingCluster: (serviceBasePath: string) =>
      serviceBasePath === env.routes.api.gw || serviceBasePath === '/gw',
    ports: mainPorts,
    getClusterPath: (serviceName: string, port?: number) =>
      `http://${serviceName}-svc.${mainClusterPath}:${port}`,
  },
  drushim: {
    basePath: env.routes.api.gwDrushim,
    isMatchingCluster: (serviceBasePath: string) =>
      serviceBasePath === env.routes.api.gwDrushim ||
      serviceBasePath === '/gw-drushim',
    ports: drushimPorts,
    getClusterPath: (serviceName: string) =>
      `http://${serviceName}-svc.${drushimClusterPath}:8080`,
  },
};

const getClusterInfo = (serviceBasePath: string) => {
  const cluster = Object.values(clustersMap).find((cluster) =>
    cluster.isMatchingCluster(serviceBasePath),
  );

  if (!cluster) {
    throw Error(
      `missing GW cluster for the service: ${serviceBasePath}, in clustersMap`,
    );
  }

  return cluster;
};

const validateStr = (str: string) => str && typeof str === 'string';

const joinServicePath = (basePath: string, serviceName: string) => {
  if (!validateStr(basePath) || !validateStr(serviceName)) {
    throw Error(
      `invalid basePath: ${basePath} or serviceName: ${serviceName} given to api-client`,
    );
  }

  const basePathStr = String(basePath);
  const basePathLastChar = basePathStr.charAt(basePathStr.length - 1);
  const hasSlash = basePathLastChar === '/';

  return hasSlash ? `${basePath}${serviceName}` : `${basePath}/${serviceName}`;
};

export const getWithSSRSupport = (basePath: string, serviceName: string) => {
  const clusterInfo = getClusterInfo(basePath);
  const port = clusterInfo.ports[serviceName];

  if (!port) {
    throw Error(
      `missing port for the service: ${serviceName}, check ssr-services-ports.json & the service port in y2 repo`,
    );
  }

  if (isBrowser || isLocal) {
    return joinServicePath(basePath, serviceName);
  }

  return clusterInfo.getClusterPath(serviceName, port);
};
